import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, UserPlus, LogIn, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  signInWithGoogle,
  signInAsGuest,
  sendEmailSignInLink,
  authService,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LanguageSwitcher,
} from '@aaas/shared';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('redirect') || '/';
  const { t } = useTranslation('portal');


  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const isProduction = import.meta.env.VITE_ENV === 'production';

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await signInWithGoogle();
      const idToken = await result.user.getIdToken();

      // Verify token with backend
      await authService.verifyToken(idToken);

      // Do NOT navigate here — LoginRoute will redirect to ?redirect= param
      // once auth state updates. Calling navigate() here changes the URL first
      // which causes LoginRoute to lose the ?redirect= param and fall back to '/'.
    } catch (error) {
      console.error('Google login failed:', error);
      setError(t('login.errors.googleFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError(t('login.errors.invalidEmail'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use VITE_APP_URL for callback to ensure correct domain
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const callbackUrl = from !== '/'
        ? `${appUrl}/auth/email-callback?redirect=${encodeURIComponent(from)}`
        : `${appUrl}/auth/email-callback`;
      await sendEmailSignInLink(email, callbackUrl);
      navigate('/auth/email-sent', { state: { email } });
    } catch (error) {
      console.error('Email sign-in failed:', error);
      setError(t('login.errors.sendLinkFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await signInAsGuest();
      const idToken = await result.user.getIdToken();

      // Verify token with backend
      await authService.verifyToken(idToken);

      // Same as Google login — let LoginRoute handle the redirect
    } catch (error) {
      console.error('Guest login failed:', error);
      setError(t('login.errors.guestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <LogIn className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('login.heading')}</h2>
          <p className="mt-2 text-sm text-gray-600">{t('login.subheading')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('login.cardTitle')}</CardTitle>
            <CardDescription>{t('login.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Email Sign-in Form */}
            <form onSubmit={handleEmailSignIn} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.emailLabel')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2"
              >
                <Mail className="w-5 h-5" />
                <span>{isLoading ? t('login.sendingButton') : t('login.sendLinkButton')}</span>
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('login.divider')}</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              variant="outline"
            >
              <User className="w-5 h-5" />
              <span>{isLoading ? t('login.googleLoadingButton') : t('login.googleButton')}</span>
            </Button>

            {!isProduction && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('login.devOnlyLabel')}</span>
                  </div>
                </div>

                <Button
                  onClick={handleGuestLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center space-x-2"
                  variant="secondary"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>{isLoading ? t('login.guestLoadingButton') : t('login.guestButton')}</span>
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">{t('login.footer')}</p>
        </div>
      </div>
    </div>
  );
}
